import React, { useState, useMemo, useCallback } from 'react';
import UnitEditForm, { EditUnit, CustomHeaUnit } from '../../edit-form/UnitEditForm';
import { uploadFile } from '../../../../../services/File';
import { useEditUnit } from '../useEditUnit';
import {
  useEditUnitMutation,
  useHeaUpdateUnitMutation,
  UnitContentFragmentDoc,
  HeaUnitContentFragmentDoc,
  useHeaCreateUnitMutation,
  useHeaDeleteUnitMutation
} from '../../../../../api/graphql-codegen';
import { formatDate } from '@src/utils/time';
import { useToast } from '@src/hooks/useToast';
import { useAWSKeys } from '@src/hooks';
import { logger } from '../../../../../utils/logger';

type Props = {
  refetchUnit?: () => Promise<void>
};

function EditUnitContent(props: Props) {
  const { refetchUnit } = props
  const [loading, setLoading] = useState(false);
  const { unit, heaUnit, setHeaUnit } = useEditUnit();
  const { successToast, errorToast } = useToast()
  const { awsKeys } = useAWSKeys();

  const [editUnit] = useEditUnitMutation({
    update(cache, res) {
      const d = res?.data?.editUnit;
      if (d) {
        cache.writeFragment({
          id: cache.identify(d),
          data: { ...d },
          fragment: UnitContentFragmentDoc,
        });
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
      errorToast('Unit update failed, Please try again.');
    },
    onCompleted(data) {
      if (data?.editUnit) {
        successToast('Unit is updated successfully!');
      }
    }
  });

  const [editHeaUnit] = useHeaUpdateUnitMutation({
    update(cache, res) {
      const d = res?.data?.heaUpdateUnit;
      if (d) {
        cache.writeFragment({
          id: cache.identify(d),
          data: { ...d },
          fragment: HeaUnitContentFragmentDoc,
        });
      }
    },
    onError(e) {
      logger.debug(`[e]`, e);
      errorToast('HomeEdUnit update failed, Please try again.');
    },
    onCompleted(data) {
      if (data?.heaUpdateUnit) {
        successToast('HomeEdUnit is updated successfully!');
      }
    }
  });

  const [heaDeleteUnit] = useHeaDeleteUnitMutation({
    onCompleted() {
      setHeaUnit(undefined);
      successToast('HEA Unit is deleted successfully!');
    },
  });

  const [createHeaUnit] = useHeaCreateUnitMutation({
    onCompleted(res) {
      if (res.heaCreateUnit) {
        successToast('HEA Unit is created successfully!');
      }
    },
    onError(e) {
      logger.debug(`[createHeaUnit error]-`, e);
      errorToast('HomeEdUnit create failed, Please try again.');
    },
  });

  const handleEdit = useCallback(async ({ coverImage, __typename, id, progress, originalUnitId, hea, alternateMaterials, ...unitObj }: EditUnit, isHomeEd?: boolean) => {
    if (!unit?.id) return;
    try {
      setLoading(true);
      const updatedUnit: EditUnit = unitObj;
      if (coverImage && typeof coverImage !== 'string') {
        updatedUnit['coverImage'] = await uploadFile(awsKeys, coverImage, 'images');
      } else {
        updatedUnit['coverImage'] = coverImage;
      }

      let unitType = 'Primary';
      if (updatedUnit.type === '0') {
        unitType = 'Primary'
      } else if (updatedUnit.type === '1') {
        unitType = 'Additional'
      } else {
        unitType = updatedUnit.type || 'Primary'
      }

      let comingSoonDate = ''
      if (updatedUnit.comingSoonAt) {
        let d;
        if (updatedUnit.comingSoonAt?.includes('-')) {
          d = new Date(updatedUnit.comingSoonAt);
        } else {
          d = new Date(parseInt(updatedUnit.comingSoonAt));
        }
        comingSoonDate = formatDate(d.valueOf(), 'yyyy-MM-dd')
      }
      try {
        if (!isHomeEd) {
          await editUnit({
            variables: {
              input: { id: unit?.id, ...updatedUnit, type: unitType, coverImage: updatedUnit['coverImage'] || undefined, default: updatedUnit.default || false, skill: `${updatedUnit.skill}`, comingSoonAt: comingSoonDate },
            }
          });
        } else if (heaUnit?.id) {
          await editHeaUnit({
            variables: {
              input: { ...updatedUnit, id: heaUnit.id, coverImage: updatedUnit['coverImage'] || undefined },
            }
          })
        }
      } catch (e) {
        logger.debug(`[e]--`, e);
      }
      if (refetchUnit) {
        await refetchUnit();
      }
      setLoading(false);
    } catch (e) {
      logger.debug(`[e]`, e);
      setLoading(false);
    }
  }, [editHeaUnit, editUnit, heaUnit?.id, refetchUnit, unit?.id]);

  const [unitContent, heaUnitContent] = useMemo(() => {
    if (unit) {
      const { teacherPreparation, studyGuides, studyGuideMaterials, studentQuizzes, unitStudyReview, classPresentation: _, ...unitContentData } = unit;
      if (heaUnit) {
        const { parentPreparation, classPresentation, ...heaUnitContentData } = heaUnit;
        return [unitContentData, heaUnitContentData]
      }
      return [unitContentData, undefined]
    }

    return [undefined, undefined]
  }, [unit, heaUnit]);

  const toggleIsHomeEd = useCallback(async (isHomeEd: boolean, customHeaUnit?: CustomHeaUnit) => {
    if (isHomeEd) {
      if (customHeaUnit) {
        setLoading(true);
        const { coverImage: heaCoverImage, __typename, ...updatedHeaUnit } = customHeaUnit;
        if (heaCoverImage && typeof heaCoverImage !== 'string') {
          updatedHeaUnit['coverImage'] = await uploadFile(awsKeys, heaCoverImage, 'images');
        } else {
          updatedHeaUnit['coverImage'] = heaCoverImage;
        }

        const response = await createHeaUnit({
          variables: {
            input: { ...updatedHeaUnit, coverImage: updatedHeaUnit['coverImage'] },
          }
        });
        setHeaUnit(response.data?.heaCreateUnit);
        if (refetchUnit) {
          await refetchUnit();
        }
        setLoading(false);
      }
    } else {
      if (heaUnit?.id) {
        setLoading(true);
        setHeaUnit(undefined);
        await heaDeleteUnit({
          variables: {
            input: { id: heaUnit?.id },
          }
        });
        setLoading(false);
      }
    }
  }, [createHeaUnit, heaDeleteUnit, heaUnit?.id, refetchUnit, setHeaUnit]);

  return (
    <UnitEditForm loading={loading} onSave={handleEdit} unit={unitContent} heaUnit={heaUnitContent} setLoading={setLoading} toggleIsHomeEd={toggleIsHomeEd} />
  );
}

export default EditUnitContent;
